import React, { useState, useEffect } from 'react';
import Sidebar from '../../components/Dashboard/Sidebar/Sidebar';
import Header from '../../components/Dashboard/Header/Header';
import { Shield, CheckCircle, XCircle, ChevronDown, ChevronUp, Loader2 } from 'lucide-react';
import api from '../../api/axios';
import toast from 'react-hot-toast';
import CustomSelect from '../../components/Common/Select/CustomSelect';
import Breadcrumbs from '../../components/Common/Breadcrumbs/Breadcrumbs';
import { useLoading } from '../../context/LoadingContext';
import styles from './UserPermissions.module.css';
import Footer from '../../components/Common/Footer/Footer';

const UserPermissions = () => {
  const { loading, showLoader, hideLoader } = useLoading();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [fetchingDropdowns, setFetchingDropdowns] = useState(false);

  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);

  const [availablePermissions, setAvailablePermissions] = useState([]);
  const [userPermissions, setUserPermissions] = useState([]);

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    setFetchingDropdowns(true);
    try {
      const [usersRes, permissionsRes] = await Promise.all([
        api.get('/users?per_page=1000', { skipLoader: true }), // Fetching only non-admin users
        api.get('/permissions', { skipLoader: true })
      ]);

      if (usersRes.data.success) {
        setUsers(usersRes.data.data.data || []);
      }

      if (permissionsRes.data.success) {
        // Filter out 'view dashboard' as it is now common/default for all users
        // Also filter out 'delete users' as per user request
        setAvailablePermissions((permissionsRes.data.data || []).filter(p =>
          p.name !== 'view dashboard' && p.name !== 'delete users'
        ));
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load initial data');
    } finally {
      setFetchingDropdowns(false);
    }
  };

  const handleUserSelect = (option) => {
    setSelectedUser(option ? option.value : null);
    if (option) {
      const user = users.find(u => u.id === option.value);
      if (user && user.permissions) {
        setUserPermissions(user.permissions.map(p => p.name));
      } else {
        setUserPermissions([]);
      }
    } else {
      setUserPermissions([]);
    }
  };

  const togglePermission = (permissionName) => {
    if (!selectedUser) return;

    setUserPermissions(prev => {
      if (prev.includes(permissionName)) {
        return prev.filter(p => p !== permissionName);
      } else {
        return [...prev, permissionName];
      }
    });
  };

  const toggleGroupPermissions = (groupPerms) => {
    if (!selectedUser) return;
    const groupNames = groupPerms.map(p => p.name);
    const allGranted = groupNames.every(n => userPermissions.includes(n));
    if (allGranted) {
      // Deselect all in this group
      setUserPermissions(prev => prev.filter(p => !groupNames.includes(p)));
    } else {
      // Select all in this group
      setUserPermissions(prev => [...new Set([...prev, ...groupNames])]);
    }
  };

  const handleSavePermissions = async () => {
    if (!selectedUser) {
      toast.error('Please select a user first');
      return;
    }

    setSaving(true);
    try {
      // Always include 'view dashboard' when saving, as it's a default requirement for all users
      const finalPermissions = [...new Set([...userPermissions, 'view dashboard'])];

      const response = await api.post(`/users/${selectedUser}/permissions`, {
        permissions: finalPermissions
      });

      if (response.data.success) {
        toast.success('Permissions updated successfully');
        // Update the local users state to reflect new permissions
        setUsers(prevUsers => prevUsers.map(user => {
          if (user.id === selectedUser) {
            return {
              ...user,
              permissions: availablePermissions.filter(p => userPermissions.includes(p.name))
            };
          }
          return user;
        }));
      }
    } catch (error) {
      console.error('Error saving permissions:', error);
      toast.error(error.response?.data?.message || 'Failed to save permissions');
    } finally {
      setSaving(false);
    }
  };

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  const toggleAllPermissions = () => {
    if (!selectedUser) return;
    const allNames = availablePermissions.map(p => p.name);
    const allGranted = allNames.every(n => userPermissions.includes(n));
    if (allGranted) {
      setUserPermissions([]);
    } else {
      setUserPermissions(allNames);
    }
  };

  // Maps sub-module names to their parent menu category
  const menuGroupMap = {
    'Employees': 'HR',
    'Departments': 'Masters',
    'Designations': 'Masters',
    'Inventory': 'Masters',
    'Units': 'Masters',
    'Invoices': 'Sales Invoices',
    'Vendor Payments': 'Finance',
    'Payments Received': 'Finance',
    'Taxes': 'Masters',
    'Project Types': 'Masters',
    'Banks': 'Masters',
    'Expense Category': 'Masters',
    'Expense Categories': 'Masters',
    'Users': 'HR',
    'Leave Types': 'Masters',
    'Advance Requests': 'Finance',
    'Loan Requests': 'Finance',
    'Holidays': 'Masters',
    'Employee Attendance': 'HR',
    'Hr Dashboard': 'HR',
    'Payroll': 'HR',
  };

  // Display order for top-level menu groups
  const menuOrder = [
    'Sales Invoices', 'Projects', 'Work Orders', 'LOIs (Letter of Intent)', 'Design Submissions',
    'Inspections', 'Material Receipts', 'Finance',
    'Vendors', 'Customers',
    'Masters', 'HR', 'Settings', 'General'
  ];

  // Build a direct hierarchy: Category -> SubModule -> Permissions 
  const buildPermissionHierarchy = () => {
    const categories = {};

    availablePermissions.forEach(perm => {
      const words = perm.name.split(' ');
      const action = words[0];

      if (words.length > 1) {
        const subjectWords = words.slice(1);
        const rawSubModule = subjectWords.map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
        const parentCategory = menuGroupMap[rawSubModule] || rawSubModule;

        if (!categories[parentCategory]) {
          categories[parentCategory] = {};
        }
        if (!categories[parentCategory][rawSubModule]) {
          categories[parentCategory][rawSubModule] = [];
        }
        categories[parentCategory][rawSubModule].push({ ...perm, action });
      } else {
        if (!categories['General']) {
          categories['General'] = { 'General': [] };
        }
        categories['General']['General'].push({ ...perm, action });
      }
    });

    // Sort by defined order
    const sorted = {};
    menuOrder.forEach(key => {
      if (categories[key]) sorted[key] = categories[key];
    });
    Object.keys(categories).forEach(key => {
      if (!sorted[key]) sorted[key] = categories[key];
    });

    return sorted;
  };

  const permissionHierarchy = buildPermissionHierarchy();

  return (
    <div className={styles['permissions-layout']}>
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

      <div className={styles['content-wrapper']}>
        <Header onMenuClick={toggleSidebar} />

        <main className={styles['main-content']}>
          <Breadcrumbs />

          <div className={styles['page-header']}>
            <div className={styles['header-title-section']}>
              <h1>Employee Permissions</h1>
              <p>Direct view: Manage and assign all user capabilities from a single grid.</p>
            </div>
            {selectedUser && (
              <div className={styles['header-actions']}>
                <button
                  className={`${styles['grant-all-btn']} ${availablePermissions.every(p => userPermissions.includes(p.name))
                    ? styles['revoke-all']
                    : ''
                    }`}
                  onClick={toggleAllPermissions}
                >
                  {availablePermissions.every(p => userPermissions.includes(p.name))
                    ? 'Revoke All Permissions'
                    : 'Grant All Permissions'}
                </button>
                <button
                  className={styles['save-header-btn']}
                  onClick={handleSavePermissions}
                  disabled={saving}
                >
                  <span>{saving ? 'Saving...' : 'Save Permissions'}</span>
                </button>
              </div>
            )}
          </div>

          <div className={styles['main-card']}>
            <div className={styles['user-selector-container']}>
              <div className={styles['icon-container']}>
                <Shield size={24} color="#f97316" />
              </div>
              <div className={styles['selector-content']}>
                <label className={styles['selector-label']}>Select User Account</label>
                <CustomSelect
                  name="user_select"
                  value={selectedUser ? {
                    value: selectedUser,
                    label: users.find(u => u.id === selectedUser)?.name || 'Selected User'
                  } : null}
                  onChange={handleUserSelect}
                  options={users.map(u => ({
                    value: u.id,
                    label: `${u.name} (${u.email})`
                  }))}
                  placeholder="Search and select user to manage permissions..."
                  isLoading={fetchingDropdowns}
                />
              </div>
            </div>

            {fetchingDropdowns && users.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '100px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
                  <Loader2 size={40} className={styles['spinner']} style={{ color: '#f97316' }} />
                  <span style={{ color: '#64748b', fontWeight: 600 }}>Fetching technical details...</span>
                </div>
              </div>
            ) : !selectedUser ? (
              <div className={styles['empty-state']}>
                <Shield size={48} color="#e2e8f0" />
                <h3>No User Selected</h3>
                <p>Select a user from the dropdown above to view and assign permissions.</p>
              </div>
            ) : (
              <div className={styles['permissions-container']}>
                {Object.keys(permissionHierarchy).map(catName => {
                  const subModules = permissionHierarchy[catName];
                  const allCatPerms = Object.values(subModules).flat();
                  const allCatGranted = allCatPerms.every(p => userPermissions.includes(p.name));

                  return (
                    <div key={catName} className={styles['category-section']}>
                      <div className={styles['category-header']}>
                        <h2 className={styles['category-title']}>{catName === 'General' ? 'Common Permissions' : catName}</h2>
                        <button
                          className={`${styles['cat-toggle-btn']} ${allCatGranted ? styles['btn-clear'] : styles['btn-select']}`}
                          onClick={() => toggleGroupPermissions(allCatPerms)}
                        >
                          {allCatGranted ? 'Deselect All' : 'Select All'}
                        </button>
                      </div>

                      <div className={styles['modules-grid']}>
                        {Object.keys(subModules).map(subName => {
                          const perms = subModules[subName];
                          const allSubGranted = perms.every(p => userPermissions.includes(p.name));

                          return (
                            <div key={subName} className={styles['module-card']}>
                              <div className={styles['module-card-header']}>
                                <h3>{subName}</h3>
                                <button
                                  className={`${styles['module-mini-btn']} ${allSubGranted ? styles['mini-btn-clear'] : styles['mini-btn-all']}`}
                                  onClick={(e) => { e.stopPropagation(); toggleGroupPermissions(perms); }}
                                >
                                  {allSubGranted ? 'Clear' : 'All'}
                                </button>
                              </div>
                              <div className={styles['perms-chip-grid']}>
                                {perms.map(perm => {
                                  const isGranted = userPermissions.includes(perm.name);
                                  return (
                                    <div
                                      key={perm.id}
                                      className={`${styles['perm-chip']} ${isGranted ? styles['active'] : ''}`}
                                      onClick={() => togglePermission(perm.name)}
                                    >
                                      {isGranted ? <CheckCircle size={14} /> : <XCircle size={14} />}
                                      <span>{perm.action}</span>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}

                <div className={styles['bottom-action']}>
                  <button
                    className={styles['save-bottom-btn']}
                    onClick={handleSavePermissions}
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : 'Save Permissions Configuration'}
                  </button>
                </div>
              </div>
            )}
          </div>

          <Footer />
        </main>
      </div>
    </div>
  );
};

export default UserPermissions;
